import { useLocations } from 'apps/wrapper/src/hooks/use-locations';
import { useProductsByLocation } from 'apps/wrapper/src/hooks/use-products-by-location';
import { useFrames } from 'apps/wrapper/src/hooks/use-wrapper-frames';
import { useEffect, useRef, useState } from 'react';
import { useEventBus } from '../../../hooks/use-event-bus';
import { useHistory, useUrlBuilder } from '../../../hooks/use-history-intercept';
import { useWrapperNavigation } from '../../../hooks/use-wrapper-navigation';
import { FrameComponent } from './frame';

enum MessageForwardType {
  JUMP_TO_MESSAGE = 'jump_to_messages'
}

type MessageForward = {
  data: {
    message_type: MessageForwardType;
    data: any;
  };
};

const Frames = () => {
  const history = useHistory();
  const { listen, publish } = useEventBus();
  const { getFrameUrl, getFrameName, windowUrls } = useWrapperNavigation();
  const { buildUrl } = useUrlBuilder();
  const { selectedLocation } = useLocations();
  const { unloadAllFrames } = useFrames();
  const { hasProduct: hasProductByLocation, isLoading } = useProductsByLocation(selectedLocation?.id);
  const [hasSentToMainApp, setHasSentToMainApp] = useState(true);

  const refMessenger = useRef<HTMLIFrameElement>(null);
  const refPhone = useRef<HTMLIFrameElement>(null);
  const refCore = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const handleLocationChange = (event: MessageForward) => {
      if (Object.values(MessageForwardType).includes(event.data.message_type)) {
        refMessenger.current?.contentWindow?.postMessage(event.data, '*');
        refPhone.current?.contentWindow?.postMessage(event.data, '*');
      }
    };

    window.addEventListener('message', handleLocationChange);

    return () => window.removeEventListener('message', handleLocationChange);
  }, [history]);

  useEffect(
    () =>
      listen('navigate', (event) => {
        if (event.messageType !== 'navigate') return;

        const eventData = event.data;
        const frame = getFrameName(eventData.pathname);

        if (frame === 'core' && eventData.crossFrame && refCore.current) {
          try {
            const url = new URL(buildUrl(getFrameUrl(frame, eventData.pathname)));

            if (selectedLocation?.id) url.searchParams.set('locationId', String(selectedLocation.id));

            refCore.current.src = String(url);
          } catch (error) {
            console.error('Error setting frame URL:', error);
          }
        }
      }),
    [listen, selectedLocation, buildUrl]
  );

  useEffect(
    () =>
      listen('location-switch-start', () => {
        unloadAllFrames();
        setHasSentToMainApp(false);
      }),
    [listen]
  );

  useEffect(() => {
    publish({ messageType: 'closeWidget' });

    if (isLoading || hasSentToMainApp) return;

    let url = '';

    if (
      hasProductByLocation('Core') ||
      hasProductByLocation('Forms') ||
      hasProductByLocation('Reminders') ||
      hasProductByLocation('WelcomePackets') ||
      hasProductByLocation('Payments') ||
      hasProductByLocation('Campaign') ||
      hasProductByLocation('Forms2')
    ) {
      url = windowUrls.core;
    } else if (hasProductByLocation('messenger')) {
      url = windowUrls.messenger1;
    } else if (hasProductByLocation('messenger2')) {
      url = windowUrls.messenger;
    }

    if (url) {
      const query = new URLSearchParams(url.split('?')[1]);
      const searchParams = new URLSearchParams(history.location.search);

      if (selectedLocation?.id) query.set('locationId', String(selectedLocation?.id));

      if (searchParams.get('customerId')) {
        query.set('customerId', searchParams.get('customerId') || '');
      }

      history.push({
        pathname: windowUrls.core,
        search: query.toString()
      });
    }

    setHasSentToMainApp(true);
  }, [isLoading, hasSentToMainApp, hasProductByLocation, selectedLocation?.id]);

  useEffect(() => {
    if (!selectedLocation?.id) return;

    history.setLocationId(String(selectedLocation.id));
  }, [selectedLocation?.id, history.setLocationId]);

  // Early return if no location is selected so it will prevent re-rendering
  if (!selectedLocation?.id) return null;

  return (
    <>
      <FrameComponent
        key={`frame-phone-${selectedLocation.id}`}
        frameName="phone"
        ref={refPhone}
        render={hasProductByLocation('phone2')}
      />
      <FrameComponent
        key={`frame-messenger-${selectedLocation.id}`}
        frameName="messenger"
        ref={refMessenger}
        render={hasProductByLocation('messenger2')}
      />
      <FrameComponent
        key={`frame-core-${selectedLocation.id}`}
        frameName="core"
        ref={refCore}
        render={
          hasProductByLocation('core') ||
          hasProductByLocation('reminders') ||
          hasProductByLocation('forms') ||
          hasProductByLocation('campaign') ||
          hasProductByLocation('forms2')
        }
      />
      <FrameComponent
        key={`frame-contacts-${selectedLocation.id}`}
        frameName="contacts"
        render={hasProductByLocation('patients2')}
      />
      <FrameComponent
        key={`frame-contacts1-${selectedLocation.id}`}
        frameName="contacts1"
        render={hasProductByLocation('patients1')}
      />
      <FrameComponent
        key={`frame-scheduler-${selectedLocation.id}`}
        frameName="scheduler"
        render={hasProductByLocation('scheduler')}
        autoActivate={true}
      />
      <FrameComponent
        key={`frame-messenger1-${selectedLocation.id}`}
        frameName="messenger1"
        render={hasProductByLocation('messenger')}
      />
      <FrameComponent
        key={`frame-phone1-${selectedLocation.id}`}
        frameName="phone1"
        render={hasProductByLocation('phone') || hasProductByLocation('phone1')}
        autoActivate={true}
      />
      <FrameComponent key={`frame-insights-${selectedLocation.id}`} frameName="insights" render={true} />
      <FrameComponent
        key={`frame-settings-${selectedLocation.id}`}
        frameName="settings"
        render={true}
        autoActivate={true}
      />
      <FrameComponent
        key={`frame-reputation-${selectedLocation.id}`}
        frameName="reputation"
        render={hasProductByLocation('reputationmanagement')}
      />
      <FrameComponent
        key={`frame-campaigns-${selectedLocation.id}`}
        frameName="campaigns"
        render={hasProductByLocation('campaign')}
      />
      <FrameComponent
        key={`frame-forms-${selectedLocation.id}`}
        frameName="forms"
        render={hasProductByLocation('forms2')}
      />
      <FrameComponent
        key={`frame-virtualvisits-${selectedLocation.id}`}
        frameName="virtualvisits"
        render={hasProductByLocation('virtualvisits')}
      />
      <FrameComponent
        key={`frame-evaluatemypractice-${selectedLocation.id}`}
        frameName="evaluatemypractice"
        render={hasProductByLocation('evaluatemypractice')}
      />
      <FrameComponent
        key={`frame-teamchat-${selectedLocation.id}`}
        frameName="teamchat"
        render={hasProductByLocation('teamchat')}
      />
    </>
  );
};

export default Frames;
